/* CoParent Console — Web Push helpers.
 *
 * v77.57 — Production-only gate + friendly preview message.
 *   • Push REQUIRES a stable HTTPS origin. Browsers will refuse to
 *     subscribe on ephemeral preview hosts (and even when they don't,
 *     deliveries are unreliable). We hard-gate subscription on the
 *     canonical production hostname.
 *   • Helpers return structured objects (`{ ok, reason, message }`)
 *     instead of throwing, so callers can render the reason in the
 *     UI without having to interpret error strings.
 *   • Subscriptions persist server-side via /api/push/subscribe — the
 *     same idempotent `(user_id, endpoint)` upsert that already
 *     existed, no DB schema change.
 */
import { api } from "./api";

/* The single canonical production host. The preview job emits hostnames like
 * `<job>.preview.emergentagent.com`; deliberately not gated on
 * `.coparentconsole.com` only, but on this exact host so a future
 * second public host (e.g. a www. subdomain) doesn't silently
 * activate push without an explicit code change. */
const PROD_HOST = "app.coparentconsole.com";

export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

/** Whether push setup should run in this environment. */
export function isPushAllowedHere() {
  if (typeof window === "undefined") return false;
  // Localhost is treated as a "secure context" by browsers and is
  // useful for dev — but we still keep production-only behaviour for
  // real subscription so we don't pollute the live VAPID-key push
  // service with throwaway endpoints. Override via REACT_APP_PUSH_ALLOW_PREVIEW
  // for QA when explicitly needed.
  const host = window.location?.hostname || "";
  if (host === PROD_HOST) return true;
  if (process.env.REACT_APP_PUSH_ALLOW_PREVIEW === "1") return true;
  return false;
}

/** Friendly explanation for when push is unavailable. */
export function pushUnavailableReason() {
  if (typeof window === "undefined") return "Browser unavailable";
  if (!isPushSupported()) {
    return "Push notifications aren't supported on this browser. Try Chrome or Firefox on desktop, or Safari 16.4+ on iOS.";
  }
  if (!window.isSecureContext) {
    return "Push notifications require a secure (HTTPS) connection.";
  }
  if (!isPushAllowedHere()) {
    return "Push notifications are only available on the live app at app.coparentconsole.com. You're currently on a preview/test environment.";
  }
  return "";
}

async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) throw new Error("Service workers unsupported");
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  return reg;
}

export async function getPushSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch (_e) {
    return null;
  }
}

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Subscribe the current user to web push.
 *  Returns `{ ok, reason?, message?, subscription? }` rather than throwing —
 *  so the caller can show a friendly toast/banner without trying to
 *  interpret raw browser error strings.
 */
export async function subscribeToPush() {
  if (!isPushSupported()) {
    return { ok: false, reason: "unsupported", message: pushUnavailableReason() };
  }
  if (!isPushAllowedHere()) {
    return { ok: false, reason: "wrong-host", message: pushUnavailableReason() };
  }
  if (!window.isSecureContext) {
    return { ok: false, reason: "insecure-context", message: pushUnavailableReason() };
  }

  let permission;
  try {
    permission = await Notification.requestPermission();
  } catch (e) {
    return { ok: false, reason: "permission-error", message: "Couldn't open the permission prompt." };
  }
  if (permission !== "granted") {
    return {
      ok: false,
      reason: "permission-denied",
      message: "You'll need to allow notifications in your browser to enable push.",
    };
  }

  let serverKey;
  let serverEnabled;
  try {
    const { data } = await api.get("/push/vapid-public-key");
    serverKey = data?.key || "";
    serverEnabled = !!data?.enabled;
  } catch (_e) {
    return { ok: false, reason: "server-unreachable", message: "Couldn't reach the server to set up push." };
  }
  if (!serverKey || !serverEnabled) {
    return {
      ok: false,
      reason: "server-not-ready",
      message: "Push isn't switched on for this deployment yet. Please try again later.",
    };
  }

  let reg;
  try {
    reg = await registerServiceWorker();
  } catch (e) {
    return { ok: false, reason: "sw-register-failed", message: "Couldn't register the background worker." };
  }

  let sub;
  try {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(serverKey),
    });
  } catch (e) {
    return {
      ok: false,
      reason: "subscribe-failed",
      message:
        "Your browser couldn't subscribe to push. This often means you're on a preview environment or a browser/profile that blocks push.",
    };
  }

  const raw = sub.toJSON();
  try {
    await api.post("/push/subscribe", {
      endpoint: raw.endpoint,
      keys: raw.keys || {},
      user_agent: navigator.userAgent || "",
    });
  } catch (_e) {
    // Server save failed — unsubscribe locally so we don't end up
    // with a browser-side subscription that the server doesn't know
    // about.
    try { await sub.unsubscribe(); } catch (_e2) { /* swallow */ }
    return { ok: false, reason: "server-save-failed", message: "Couldn't save the subscription." };
  }

  return { ok: true, subscription: sub };
}

export async function unsubscribeFromPush() {
  const sub = await getPushSubscription();
  if (!sub) return true;
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  await api.delete("/push/subscribe", { params: { endpoint } }).catch(() => {});
  return true;
}
